// rockSpecies.ts — what KIND of rock this is.
//
// The scatter until now drew one rock, everywhere, in one colour. That is
// defensible for a single patch of ground and wrong for a planet: Gediz Vallis
// Ridge is a debris flow, boulders carried by water three billion years ago and
// piled up, so a single frame there holds rounded fluvial cobbles beside angular
// blocks beside the knife-edged ventifacts Curiosity spent months driving around.
// A crater floor beside a delta and a volcanic outflow channel should not be
// made of the same stone.
//
// ── Where this sits relative to the measurement ──────────────────────────────
//
// The rock ABUNDANCE is measured — counted off the orthoimage per plot, with a
// stability test that refuses the number when the picture will not support it
// (tools/dtm/rocks.py). None of that is true here. Which lithologies a site
// carries is ART DIRECTION informed by published geology, and it is kept in a
// separate file with its own vocabulary so the two can never be mistaken for
// each other. A species palette is a claim about what the ground is made of; the
// abundance is a claim about how much of it there is. Only the second is
// measured, and the code should make that obvious at a glance.

/** One lithology: how it breaks, and how it looks once broken. */
export interface RockSpecies {
  id: string;
  /** For the panel and the markdown dump. */
  name: string;

  // ── shape ────────────────────────────────────────────────────────────────
  /** Amplitude of the three displacement octaves. Big first value = lumpy at
   *  the silhouette scale; big third = chippy at the surface. */
  octaves: [number, number, number];
  /** 0 keeps the lumps smooth, 1 quantises the radius into flat fracture
   *  planes. Rounded river cobbles want 0; conchoidal basalt wants 1. */
  facet: number;
  /** Vertical squash. Low values give platy slabs, high gives blocks. */
  squash: number;
  /** Long axis stretch, applied about a random horizontal heading. Ventifacts
   *  and slabs are not equidimensional. */
  elongate: number;

  // ── surface ──────────────────────────────────────────────────────────────
  /** Base reflectance. Mars is not one colour: basalt is near-black, the
   *  sulfate and mudstone units are strikingly pale. */
  albedo: [number, number, number];
  /** Strength of the triplanar mottling. */
  mottle: number;
  /** Frequency of the surface grain, in cycles per metre. */
  grain: number;
  /** How readily regolith collects on the up-faces. A fresh ventifact is swept
   *  clean by the same wind that carved it; a rough breccia holds dust. */
  dust: number;

  // ── bedding ──────────────────────────────────────────────────────────────
  /** Strength of visible laminae on the rock's cut faces, 0 = massive.
   *
   *  Whale Rock is the reference: a sedimentary block's edge is a stack of thin
   *  layers, and that stripe is most of what says "this was deposited" rather
   *  than "this cooled". A solid-coloured lump cannot say it. */
  bedding: number;
  /** Laminae across the rock's own height. Higher = finer layering. */
  beddingCount: number;
  /** True if the rock lies in the REGIONAL bed rather than tumbling.
   *
   *  This is the Kimberley distinction. Float rock has been moved and points
   *  any way it likes; an exposed bed has not, and every plate in it shares one
   *  dip. Scattering bedrock with random rotations reads instantly as rubble,
   *  which is exactly wrong for an outcrop. */
  bedded: boolean;
}

/**
 * The species themselves.
 *
 * Each is a real Martian rock type, described the way it would be described in
 * the field, because that is the only way to tell later whether the parameters
 * are wrong or merely unfamiliar.
 */
export const SPECIES: Record<string, RockSpecies> = {
  basalt: {
    id: 'basalt',
    name: 'Basalt block',
    // The default Martian float rock. Breaks conchoidally into angular blocks
    // with sharp arrises; dark enough to read as a hole in the dust.
    octaves: [0.62, 0.28, 0.11],
    facet: 1.0,
    squash: 0.78,
    elongate: 1.15,
    albedo: [0.19, 0.145, 0.115],
    mottle: 0.40,
    grain: 11.0,
    dust: 0.30,
    bedding: 0.0,
    beddingCount: 0,
    bedded: false,
  },
  ventifact: {
    id: 'ventifact',
    name: 'Ventifact',
    // Wind-carved. Long facets aligned with the prevailing wind and edges sharp
    // enough that MSL's team named them gator-backs and drove around them. The
    // same wind that cuts them keeps them swept, so almost no dust settles.
    octaves: [0.46, 0.34, 0.06],
    facet: 1.0,
    squash: 0.62,
    elongate: 2.10,
    albedo: [0.165, 0.128, 0.104],
    mottle: 0.28,
    grain: 6.0,
    dust: 0.10,
    bedding: 0.12,
    beddingCount: 5,
    bedded: false,
  },
  platy: {
    id: 'platy',
    name: 'Layered plate',
    // Bedded sediment splitting along its laminae — flat, broad, pale, and
    // lying rather than sitting. Jezero's crater floor and Gale's lower units
    // are full of them.
    octaves: [0.34, 0.16, 0.07],
    facet: 0.75,
    squash: 0.34,
    elongate: 1.55,
    albedo: [0.315, 0.262, 0.208],
    mottle: 0.30,
    grain: 14.0,
    dust: 0.55,
    // The Whale Rock signature: many fine laminae, strongly expressed, and
    // lying in the regional bed rather than tumbled.
    bedding: 0.85,
    beddingCount: 16,
    bedded: true,
  },
  cobble: {
    id: 'cobble',
    name: 'Rounded float',
    // Carried by water and rounded on the way. This is the Gediz Vallis
    // signature: a debris flow leaves cobbles that are visibly TRAVELLED,
    // sitting among angular blocks that plainly are not.
    octaves: [0.44, 0.14, 0.04],
    facet: 0.0,
    squash: 0.86,
    elongate: 1.10,
    albedo: [0.245, 0.192, 0.152],
    mottle: 0.22,
    grain: 9.0,
    dust: 0.42,
    bedding: 0.0,
    beddingCount: 0,
    bedded: false,
  },
  breccia: {
    id: 'breccia',
    name: 'Impact breccia',
    // Shattered and welded back together, so the surface fights itself at every
    // scale. High mottling is the whole character.
    octaves: [0.70, 0.40, 0.20],
    facet: 0.55,
    squash: 0.82,
    elongate: 1.25,
    albedo: [0.225, 0.176, 0.140],
    mottle: 0.70,
    grain: 16.0,
    dust: 0.48,
    bedding: 0.0,
    beddingCount: 0,
    bedded: false,
  },
};

/** A weighted mix. Weights are relative and need not sum to anything. */
export type RockPalette = Array<{ species: string; weight: number }>;

/**
 * Which stone each site is made of.
 *
 * Keyed by the leading part of the bake slug, so a site's detail tiles inherit
 * their region's geology — `jezero-delta/tiles/-1_3` resolves through
 * `jezero-delta`.
 */
export const SITE_PALETTES: Record<string, RockPalette> = {
  // A crater floor beside a river delta. Water put much of this here, so
  // travelled cobbles lead, with basalt float from the surrounding plains and
  // the pale bedded plates the floor is cut into.
  'jezero-delta': [
    { species: 'cobble', weight: 0.38 },
    { species: 'basalt', weight: 0.34 },
    { species: 'platy', weight: 0.20 },
    { species: 'breccia', weight: 0.08 },
  ],
  // Volcanic outflow country: basalt, and enough wind over enough time to carve
  // a good share of it. Almost nothing here was gently transported.
  'tharsis-chain': [
    { species: 'basalt', weight: 0.52 },
    { species: 'ventifact', weight: 0.28 },
    { species: 'breccia', weight: 0.14 },
    { species: 'cobble', weight: 0.06 },
  ],
  // Canyon-floor layered deposits: pale bedded sulfate plates the wind has been
  // carving into yardangs, with landslide rubble and basalt float off the walls.
  'west-candor': [
    { species: 'platy', weight: 0.40 },
    { species: 'ventifact', weight: 0.25 },
    { species: 'breccia', weight: 0.20 },
    { species: 'basalt', weight: 0.15 },
  ],
  // Foothills of a layered mound: bedded mudstone plates over a basalt sand
  // floor, wind-carved where it stands proud, with breccia shed off the ridges.
  'gale-marias-pass': [
    { species: 'platy', weight: 0.35 },
    { species: 'basalt', weight: 0.30 },
    { species: 'ventifact', weight: 0.20 },
    { species: 'breccia', weight: 0.15 },
  ],
  // A ridge on a canyon wall: basalt and landslide breccia lead, with the
  // wind-cut and travelled remainder that settles on the shelves.
  'coprates-ridge': [
    { species: 'basalt', weight: 0.45 },
    { species: 'breccia', weight: 0.30 },
    { species: 'ventifact', weight: 0.15 },
    { species: 'cobble', weight: 0.10 },
  ],
};

/** Sensible mix for ground with no declared palette. */
export const DEFAULT_PALETTE: RockPalette = [
  { species: 'basalt', weight: 0.55 },
  { species: 'cobble', weight: 0.25 },
  { species: 'breccia', weight: 0.20 },
];

/** The palette for a bake slug, falling back through the site to the default. */
export function paletteFor(slug: string | undefined): RockPalette {
  if (!slug) return DEFAULT_PALETTE;
  const site = slug.split('/')[0] ?? slug;
  return SITE_PALETTES[site] ?? SITE_PALETTES[slug] ?? DEFAULT_PALETTE;
}

export function paletteMarkdown(slug: string | undefined): string {
  const p = paletteFor(slug);
  const total = p.reduce((a, b) => a + b.weight, 0) || 1;
  return [
    '| species | share |',
    '|---|---|',
    ...p.map((e) => `| ${SPECIES[e.species]?.name ?? e.species} | ${(100 * e.weight / total).toFixed(0)}% |`),
  ].join('\n');
}
